import { useLayoutEffect, useRef, useState } from 'react'
import { Check, X, AlertCircle, Bug, Clock, KeyRound, Pause } from 'lucide-react'
import Spinner from './Spinner'
import { pauseTooltip, pausedStatus, type ProjectPause } from '../lib/projectPause'
import { BUG_FOUND_RUN_STATUS, OUTCOME_STATUS_LABELS } from '../guidance/journey/run-status'
import './StatusIndicator.css'

interface StatusIndicatorProps {
  status: string
  /** Prefix the label with a status glyph (check / spinner / alert), as the run tables do. */
  withIcon?: boolean
  /**
   * The project's pause, when the caller knows it. A paused project's stored rows still claim to be
   * in flight, so the badge shows 'paused' instead and its tooltip names the reason — see lib/
   * projectPause.ts. Omit it and the stored status renders as-is.
   */
  pause?: ProjectPause | null
}

const STATUS_CONFIG: Record<string, { label: string; tooltip: string; className: string }> = {
  completed: {
    label: 'Completed',
    tooltip: 'Run finished successfully',
    className: 'status-indicator--completed',
  },
  failed: {
    label: 'Failed',
    tooltip: 'Run did not pass',
    className: 'status-indicator--failed',
  },
  [BUG_FOUND_RUN_STATUS]: {
    label: OUTCOME_STATUS_LABELS.bug,
    tooltip: 'Run found a bug in the app',
    className: 'status-indicator--bug-found',
  },
  'in-progress': {
    label: 'In Progress',
    tooltip: 'Run is currently executing',
    className: 'status-indicator--in-progress',
  },
  // A run suspended at a target-site access challenge (the chat's access request card): the container
  // is up but QA is waiting for the user to fix access. Not the terminal 'blocked' below — the run
  // resumes (or ends) once the wait resolves.
  'awaiting-access': {
    label: 'Waiting on Access',
    tooltip: 'QA is stopped at a sign-in or bot challenge on the target site — resolve the access request in QA chat to continue',
    className: 'status-indicator--blocked',
  },
  paused: {
    label: 'Paused',
    // Fallback copy for a caller that knows the status is 'paused' but not which pause caused it; when
    // the pause is passed in, pauseTooltip() replaces this with the reason and the way out of it.
    tooltip: 'The project is paused, so this run is not currently executing',
    className: 'status-indicator--paused',
  },
  queued: {
    label: 'Queued',
    tooltip: 'Run is waiting for a worker',
    className: 'status-indicator--queued',
  },
  // A pull-request round that is held before it starts: the commit is known but its preview deployment
  // URL is not, and QA never runs a PR against anything else. Distinct from 'in-progress', which a
  // PR's pending approval would otherwise render as — nothing is executing yet.
  'awaiting-deployment': {
    label: 'Awaiting Deployment',
    tooltip: 'No deployment has been detected for this commit yet, so QA has not started',
    className: 'status-indicator--queued',
  },
  'infra-failed': {
    label: 'Infra Failed',
    tooltip: 'Run hit a transient infrastructure error and will be retried',
    className: 'status-indicator--failed',
  },
  continued: {
    label: 'Continued', tooltip: 'This attempt saved its repairs and recording; execution continues in a fresh task',
    className: 'status-indicator--queued',
  },
  incomplete: {
    label: 'Incomplete',
    tooltip: 'Run could not complete the journey, but no error occurred',
    className: 'status-indicator--incomplete',
  },
  // The run stopped at an obstacle it cannot pass (docs/backend/test-runs.md `blocked`). Terminal and not
  // retried. Mirrors the blocked status an exploration reports.
  blocked: {
    label: 'Blocked',
    tooltip: 'The run stopped at an obstacle it cannot pass on its own',
    className: 'status-indicator--blocked',
  },
  cancelled: {
    label: 'Cancelled',
    tooltip: 'Run was cancelled before it finished',
    className: 'status-indicator--incomplete',
  },
}

function StatusGlyph({ status }: { status: string }) {
  if (status === 'in-progress') return <Spinner size={12} />
  if (status === 'paused') return <Pause size={12} aria-hidden />
  if (status === 'queued' || status === 'awaiting-deployment') return <Clock size={12} aria-hidden />
  if (status === 'awaiting-access') return <KeyRound size={12} aria-hidden />
  if (status === 'completed') return <Check size={12} aria-hidden />
  if (status === 'failed' || status === 'infra-failed') return <X size={12} aria-hidden />
  if (status === BUG_FOUND_RUN_STATUS) return <Bug size={12} aria-hidden />
  return <AlertCircle size={12} aria-hidden />
}

export default function StatusIndicator({ status, withIcon = false, pause = null }: StatusIndicatorProps) {
  // The bubble is positioned in VIEWPORT coordinates rather than relative to the badge, because several
  // of the lists these badges sit in clip their contents to a rounded card (the journey groups on a run
  // page, for one) — an absolutely positioned tooltip is cut in half there, which is exactly where the
  // reason for a pause needs to be readable. Measured on hover, so no layout work happens until then.
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const badgeRef = useRef<HTMLSpanElement>(null)
  // Nudge the bubble back on-screen when centering it on the badge would push it past a window edge —
  // the Status column sits at the right edge of every run table, which is exactly where the pause
  // reason would otherwise lose its last few words.
  const tooltipRef = useRef<HTMLSpanElement>(null)
  const [shift, setShift] = useState(0)
  const displayed = pausedStatus(status, pause)
  const config = STATUS_CONFIG[displayed] ?? {
    label: displayed.charAt(0).toUpperCase() + displayed.slice(1),
    tooltip: '',
    className: '',
  }
  const tooltip = displayed === 'paused' ? pauseTooltip(pause) ?? config.tooltip : config.tooltip

  useLayoutEffect(() => {
    if (!anchor) {
      setShift(0)
      return
    }
    const rect = tooltipRef.current?.getBoundingClientRect()
    if (!rect) return
    const margin = 8
    const overflowRight = rect.right - (window.innerWidth - margin)
    const overflowLeft = margin - rect.left
    if (overflowRight > 0) setShift(current => current - overflowRight)
    else if (overflowLeft > 0) setShift(current => current + overflowLeft)
  }, [anchor])

  return (
    <span
      ref={badgeRef}
      className={`status-indicator ${config.className}${withIcon ? ' status-indicator--with-icon' : ''}`}
      data-testid={`status-indicator-${displayed}`}
      onMouseEnter={() => {
        const rect = badgeRef.current?.getBoundingClientRect()
        if (rect) setAnchor({ top: rect.top, left: rect.left + rect.width / 2 })
      }}
      onMouseLeave={() => setAnchor(null)}
    >
      {withIcon && <StatusGlyph status={displayed} />}
      {config.label}
      {anchor && tooltip && (
        <span
          ref={tooltipRef}
          className="status-indicator__tooltip"
          style={{ top: anchor.top, left: anchor.left + shift }}
          data-testid={`status-tooltip-${displayed}`}
        >
          {tooltip}
        </span>
      )}
    </span>
  )
}
